import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Container,
  Grid,
  Button,
  Typography,
  CircularProgress,
  MenuItem,
  FormControl,
  InputLabel,
  Select as MuiSelect,
  FormHelperText,
} from '@mui/material';
import { FiEye, FiEyeOff } from 'react-icons/fi';
import { toast } from 'react-toastify';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

import Page from '../../../components/Page';
import { Select, TextField } from 'src/components';
import { useFormik } from 'formik';
import { editProfileValidationSchema, studentValidationSchema } from 'src/validations/teacher';
import { studentService } from 'src/services/student.service';
import '../../styles/addstudent.css';
import { Studentimg } from 'src/images';
import palette from 'src/theme/palette';
import theme from 'src/theme/theme';
import { useAuth } from 'src/context/auth/AuthContext';
const GRADE_OPTIONS = [
  // from 1 to 12
  { value: '1', label: 'Grade 1' },
  { value: '2', label: 'Grade 2' },
  { value: '3', label: 'Grade 3' },
  { value: '4', label: 'Grade 4' },
  { value: '5', label: 'Grade 5' },
  { value: '6', label: 'Grade 6' },
  { value: '7', label: 'Grade 7' },
  { value: '8', label: 'Grade 8' },
  { value: '9', label: 'Grade 9' },
  { value: '10', label: 'Grade 10' },
  { value: '11', label: 'Grade 11' },
  { value: '12', label: 'Grade 12' },
];

interface EditStudentFormValues {
  firstName: string;
  lastName: string;
  email: string;
  studentId: string;
  grade: string;
  photourl?: string;
  phoneNumber: string;
  balance: string;
}

export default function EditStudent() {
  const navigate = useNavigate();
  const { studentId } = useParams<{ studentId: string }>();
  const queryClient = useQueryClient();
  const { getBaseRoute } = useAuth();

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [fileError, setFileError] = useState('');
  const [loading, setLoading] = useState(false);
  const [profilePic, setProfilePic] = useState<File | null>(null);

  // Fetch student data
  const {
    data: student,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['student', studentId],
    queryFn: () => studentService.getStudentById(studentId as string),
    enabled: !!studentId,
  });

  // Update student mutation
  const updateMutation = useMutation({
    mutationFn: (values: EditStudentFormValues) =>
      studentService.updateStudent(studentId as string, {
        firstname: values.firstName,
        lastname: values.lastName,
        email: values.email,
        grade: values.grade,
        phonenumber: values.phoneNumber,
        balance: parseFloat(values.balance),
        ...(values.photourl && { photourl: values.photourl }),
      }),
    onSuccess: () => {
      toast.success('Student updated successfully');
      queryClient.invalidateQueries({ queryKey: ['students'] });
      queryClient.invalidateQueries({ queryKey: ['student', studentId] });
      navigate(getBaseRoute(`/view-student/${studentId}`));
    },
    onError: (error) => {
      console.error('Error updating student:', error);
      toast.error('Failed to update student');
    },
  });

  const formik = useFormik<EditStudentFormValues>({
    initialValues: {
      firstName: student?.user?.firstname || '',
      lastName: student?.user?.lastname || '',
      email: student?.user?.email || '',
      studentId: student?.user?.extendedproperties?.studentId || '',
      grade: `${student?.grade}` || '',
      photourl: student?.user?.photourl,
      phoneNumber: student?.user?.extendedproperties?.phone || '',
      balance: `${student?.balance || 0}`,
    },
    validationSchema: editProfileValidationSchema,
    enableReinitialize: true,
    onSubmit: async (values) => {
      updateMutation.mutate(values);
    },
  });

  const handleProfilePicChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files[0]) {
      handleProfilePicUpload(event.target.files[0]);
      // setProfilePic(event.target.files[0]);
    }
  };

  const handleProfilePicUpload = async (file: File) => {
    if (!file || !studentId) return;

    try {
      setLoading(true);
      const publicUrl = await studentService.updateProfilePicture(student.user.id, file);

      formik.setFieldValue('photourl', publicUrl);
      // Optionally refetch student data to update UI
      // queryClient.invalidateQueries(['student', studentId]);
    } catch (error) {
      toast.error('Failed to update profile picture');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteProfilePic = async () => {
    if (!studentId || !formik.values.photourl) return;

    try {
      await studentService.deleteProfilePicture(student.user.id);

      formik.setFieldValue('photourl', null);
    } catch (error) {
      toast.error('Failed to delete profile picture');
    } finally {
      setLoading(false);
    }
  };

  if (isLoading) {
    return (
      <Container sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Container>
    );
  }

  if (error) {
    toast.error('Failed to fetch student data');
    return <Typography>Error loading student data</Typography>;
  }

  return (
    <Page title="Edit Student">
      <Container className="_container" maxWidth>
        <Typography variant="h4">Edit Student</Typography>
        <div className="_divider" />
        <Grid container spacing={2}>
          <Grid item xs={12} sm={2} md={2} lg={1}>
            {formik.values.photourl ? (
              <img
                src={formik.values.photourl}
                style={{ height: 100, width: 100, borderRadius: 100, border: '1px solid #ccc' }}
              />
            ) : (
              <div
                style={{
                  width: 100,
                  height: 100,
                  borderRadius: 100,
                  marginRight: 20,
                  marginTop: 10,
                  border: '1px solid #ccc',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 40,
                  color: '#ccc',
                  backgroundColor: '#f0f0f0',
                }}
              >
                {formik.values.firstName.charAt(0)}
              </div>
            )}
          </Grid>
          <Grid item xs={12} sm={10} md={10} lg={10}>
            <Typography variant="subtitle1" color="text.secondary">
              Only support .JPG, .JPEG, or .PNG file.
            </Typography>

            <div className="upload_row">
              <input
                type="file"
                id="_upload_pic"
                onChange={handleProfilePicChange}
                accept="image/*"
                style={{ display: 'none' }}
              />
              <label className="_btn" htmlFor="_upload_pic" style={{ marginTop: 0 }}>
                {loading ? ' Uploading...' : '  Upload New photo'}
              </label>
              {formik.values.photourl && (
                <button
                  type="button"
                  className="_btn"
                  style={{ backgroundColor: 'var(--white)', color: 'var(--primary-main)', marginTop: 0 }}
                  onClick={handleDeleteProfilePic}
                >
                  Delete Photo
                </button>
              )}
            </div>
            {fileError && (
              <Typography style={{ fontSize: 10, fontWeight: 700, marginTop: 10 }} color="error">
                {fileError}
              </Typography>
            )}
          </Grid>
        </Grid>
        <br />

        <form onSubmit={formik.handleSubmit}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                label="First Name"
                placeholder="Enter First Name"
                value={formik.values.firstName}
                onChange={formik.handleChange}
                name="firstName"
                error={Boolean(formik.touched.firstName && formik.errors.firstName)}
                helperText={formik.touched.firstName && formik.errors.firstName}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Last Name"
                placeholder="Enter Last Name"
                value={formik.values.lastName}
                onChange={formik.handleChange}
                name="lastName"
                error={Boolean(formik.touched.lastName && formik.errors.lastName)}
                helperText={formik.touched.lastName && formik.errors.lastName}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Email"
                placeholder="Enter Email"
                value={formik.values.email}
                onChange={formik.handleChange}
                name="email"
                error={Boolean(formik.touched.email && formik.errors.email)}
                helperText={formik.touched.email && formik.errors.email}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Student Id"
                placeholder="Enter Student Id"
                value={formik.values.studentId}
                onChange={formik.handleChange}
                name="studentId"
                error={Boolean(formik.touched.studentId && formik.errors.studentId)}
                helperText={formik.touched.studentId && formik.errors.studentId}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <label className="_input_label" style={{ marginBottom: 10, display: 'inline-block' }}>
                Grade
              </label>
              <FormControl fullWidth error={Boolean(formik.touched.grade && formik.errors.grade)} variant="outlined">
                <MuiSelect
                  labelId="grade-select-label"
                  id="grade-select"
                  name="grade"
                  value={formik.values.grade}
                  onChange={formik.handleChange}
                  className="_custom_input"
                  onBlur={formik.handleBlur}
                  style={{ paddingLeft: 0, fontWeight: 'bold' }}
                  displayEmpty
                  sx={{
                    // height: 40,
                    height: 50,
                    '&.Mui-focused': {
                      border: `1px solid ${palette.primary.main}`,
                      height: 50,
                      outline: 'none',
                      '& fieldset': {
                        border: 'none',
                      },
                    },

                    '& .MuiOutlinedInput-notchedOutline': {
                      // borderColor: 'rgba(145, 158, 171, 0.32)',
                      legend: {
                        display: 'none',
                      },
                    },
                    '& .MuiSelect-select': {
                      // paddingTop: '8px',
                      // paddingBottom: '8px',
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'text.primary',
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'primary.main',
                    },
                    '&.Mui-error .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'error.main',
                    },
                    backgroundColor: 'background.paper',
                  }}
                  renderValue={(selected) => {
                    if (!selected) {
                      return <Typography color="text.secondary">Select Grade</Typography>;
                    }
                    const option = GRADE_OPTIONS.find((opt) => opt.value === selected);
                    return option ? option.label : '';
                  }}
                >
                  {GRADE_OPTIONS.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </MuiSelect>
                {formik.touched.grade && formik.errors.grade && (
                  <FormHelperText error>{formik.errors.grade}</FormHelperText>
                )}
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Password"
                placeholder="Enter Password"
                value={formik.values.password}
                onChange={formik.handleChange}
                name="password"
                error={Boolean(formik.touched.phoneNumber && formik.errors.phoneNumber)}
                helperText={formik.touched.phoneNumber && formik.errors.phoneNumber}
                right={
                  showPassword ? (
                    <FiEye color="#718096" size={20} onClick={() => setShowPassword(!showPassword)} />
                  ) : (
                    <FiEyeOff onClick={() => setShowPassword(!showPassword)} color="#718096" size={20} />
                  )
                }
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Confirm Password"
                placeholder="Re-Enter Password"
                value={formik.values.password}
                onChange={formik.handleChange}
                name="password"
                error={Boolean(formik.touched.phoneNumber && formik.errors.phoneNumber)}
                helperText={formik.touched.phoneNumber && formik.errors.phoneNumber}
                right={
                  showConfirmPassword ? (
                    <FiEye color="#718096" size={20} onClick={() => setShowConfirmPassword(!showConfirmPassword)} />
                  ) : (
                    <FiEyeOff onClick={() => setShowConfirmPassword(!showConfirmPassword)} color="#718096" size={20} />
                  )
                }
              />
            </Grid>
          </Grid>

          <div style={{ marginTop: 20, display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
            <Button
              type="button"
              variant="outlined"
              onClick={() => navigate(studentId ? getBaseRoute(`/view-student/${studentId}`) : getBaseRoute('/students'))}
              disabled={updateMutation.isPending}
              style={{ width: 120, height: 48, color: 'var(--primary-main)' }}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={updateMutation.isPending}
              variant="contained"
              style={{ width: 120, height: 48 }}
            >
              {updateMutation.isPending ? <CircularProgress size={24} /> : 'Save'}
            </Button>
            {/* <Button variant="outlined" onClick={() => navigate('/admin/students')} disabled={updateMutation.isPending}>
              Cancel
            </Button>
            <Button variant="contained" color="primary" type="submit" disabled={updateMutation.isPending}>
              {updateMutation.isPending ? <CircularProgress size={24} /> : 'Update Student'}
            </Button> */}
          </div>
        </form>
      </Container>
    </Page>
  );
}
